import type {
  TypographyVariant,
  TypographyVariantConfig,
  TypographyVariantComponentMap,
  BoldTypographyVariant,
  BaseTypographyVariant,
} from '@iheartjane/reefer';
import { BASE_CONFIG } from '@iheartjane/reefer';

const FONT_WEIGHT_500 = 500;
const FONT_WEIGHT_600 = 600;

/**
 * Desktop styles match Kiosk typography. Mobile styles (iPads) mirror the
 * legacy POS variants: e.g. small-body uses Desktop/Body (16px) rather than Mini.
 */
const KioskBaseVariantConfig: TypographyVariantConfig<BaseTypographyVariant> = {
  'h1-regular': {
    ...BASE_CONFIG.components.Typography.variants['h1-regular'],
    fontWeight: FONT_WEIGHT_500,
    desktopStyles: {
      fontSize: '64px',
      lineHeight: '76px',
    },
    mobileStyles: {
      fontSize: '36px',
      lineHeight: '40px',
    },
  },
  'h2-regular': {
    ...BASE_CONFIG.components.Typography.variants['h2-regular'],
    fontWeight: FONT_WEIGHT_500,
    desktopStyles: {
      fontSize: '36px',
      lineHeight: '44px',
    },
    mobileStyles: {
      fontSize: '36px',
      lineHeight: '40px',
    },
  },
  'h3-regular': {
    ...BASE_CONFIG.components.Typography.variants['h3-regular'],
    desktopStyles: {
      fontSize: '28px',
      lineHeight: '36px',
    },
    mobileStyles: {
      fontSize: '24px',
      lineHeight: '32px',
    },
  },
  'h4-regular': {
    ...BASE_CONFIG.components.Typography.variants['h4-regular'],
    desktopStyles: {
      fontSize: '24px',
      lineHeight: '30px',
    },
    mobileStyles: {
      fontSize: '24px',
      lineHeight: '30px',
    },
  },
  'body-regular': {
    ...BASE_CONFIG.components.Typography.variants['body-regular'],
    desktopStyles: {
      fontSize: '22px',
      lineHeight: '28px',
    },
    mobileStyles: {
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  'small-body-regular': {
    ...BASE_CONFIG.components.Typography.variants['small-body-regular'],
    desktopStyles: {
      fontSize: '18px',
      lineHeight: '22px',
    },
    mobileStyles: {
      fontSize: '16px',
      lineHeight: '24px',
    },
  },
  'caps-regular': {
    ...BASE_CONFIG.components.Typography.variants['caps-regular'],
    letterSpacing: '0.1em',
    desktopStyles: {
      fontSize: '18px',
      lineHeight: '22px',
    },
    mobileStyles: {
      fontSize: '13px',
      lineHeight: '16px',
    },
  },
};

const KioskBoldVariantConfig: TypographyVariantConfig<BoldTypographyVariant> =
  Object.keys(KioskBaseVariantConfig)
    .map((value) => ({
      [value.replace('-regular', '-semibold')]: {
        ...KioskBaseVariantConfig[value as BaseTypographyVariant],
        fontWeight: FONT_WEIGHT_600,
      },
    }))
    .reduce((prev, curr) => ({
      ...prev,
      ...curr,
    })) as TypographyVariantConfig<BoldTypographyVariant>;

export const KioskTypographyVariantConfig: TypographyVariantConfig<TypographyVariant> =
  {
    ...KioskBaseVariantConfig,
    ...KioskBoldVariantConfig,
  };

export const KioskVariantComponentMap: TypographyVariantComponentMap<TypographyVariant> =
  {
    'h1-semibold': 'h1',
    'h1-regular': 'h1',
    'h2-semibold': 'h2',
    'h2-regular': 'h2',
    'h3-semibold': 'h3',
    'h3-regular': 'h3',
    'h4-semibold': 'h4',
    'h4-regular': 'h4',
    'body-semibold': 'p',
    'body-regular': 'p',
    'small-body-semibold': 'p',
    'small-body-regular': 'p',
    'caps-semibold': 'p',
    'caps-regular': 'p',
  };
